import React, { useState } from 'react';
import { Plus, Search, Filter, User as UserIcon, Mail, Phone, MoreVertical, Check, X, Eye, Edit2, Lock, Trash2, UserCog } from 'lucide-react';
import {
  useUsers,
  useToggleUserStatus,
  useResetPassword,
  useDeleteUser,
  getUserFullName,
  getUserInitials,
  formatLastLogin,
  getDepartments,
  getRoles,
  type User,
  type UserStatus
} from '../../../services/queries/userQueries';
import { DevModeBanner } from '../../common/DevModeBanner';
import { CreateUserModal } from './CreateUserModal';
import { EditUserModal } from './EditUserModal';
import { UserProfileModal } from './UserProfileModal';
import { useToast } from '../../../contexts/ToastContext';
import { useSetupChanges } from '../../../hooks/useSetupChanges';
import { useSwitchUser } from '../../../contexts/SwitchUserContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

export const UsersHome: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus>('all');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('bottom');
  const [menuCoordinates, setMenuCoordinates] = useState<{ top: number; left: number } | null>(null);

  const { data: users, isLoading, error } = useUsers({
    searchQuery,
    status: statusFilter,
    roleId: roleFilter,
    department: departmentFilter
  });

  const toggleStatusMutation = useToggleUserStatus();
  const resetPasswordMutation = useResetPassword();
  const deleteUserMutation = useDeleteUser();
  const { success, error: showError } = useToast();
  const { logChange } = useSetupChanges();
  const { switchToUser } = useSwitchUser();

  const departments = getDepartments();
  const roles = getRoles();

  // PE-762: Handle View As User action
  const handleViewAsUser = (user: User) => {
    const confirmed = window.confirm(
      `View as ${getUserFullName(user)}?\n\n` +
      `You will see the system with their permissions and role: ${user.roleName || 'No role assigned'}\n\n` +
      `Click "Switch Back to Admin" in the banner to return.`
    );

    if (confirmed) {
      // Get current admin info
      const currentAdmin = {
        id: 'admin-current',
        name: 'System Administrator',
        email: 'admin@regalcredit.com',
        role: 'System Administrator',
      };

      // Switch to the selected user
      switchToUser(
        {
          id: user.id,
          name: getUserFullName(user),
          email: user.email,
          role: (user.roleNames && user.roleNames.length > 0) ? user.roleNames.join(', ') : (user.roleName || 'Standard User'),
        },
        currentAdmin
      );

      // Log the action (using 'updated' as closest matching action type)
      logChange({
        action: 'updated',
        entityType: 'user',
        entityName: getUserFullName(user),
        description: `Admin started viewing as user ${getUserFullName(user)}`,
        performedBy: 'Admin'
      });

      success('Switched to user view', `Now viewing as ${getUserFullName(user)}`);

      // Navigate to home to see user's perspective
      window.location.href = '/';
    }
  };

  const activeUsers = users?.filter(u => u.isActive).length || 0;
  const inactiveUsers = users?.filter(u => !u.isActive).length || 0;

  const handleToggleStatus = async (userId: string) => {
    try {
      const user = users?.find(u => u.id === userId);
      if (!user) return;

      const newStatus = !user.isActive;
      await toggleStatusMutation.mutateAsync(userId);

      // Track user activation/deactivation
      logChange({
        action: newStatus ? 'activated' : 'deactivated',
        entityType: 'user',
        entityName: getUserFullName(user),
        description: `${newStatus ? 'activated' : 'deactivated'} user ${getUserFullName(user)}`,
        performedBy: 'Admin'
      });

      setUserMenuOpen(null);
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!window.confirm('Send password reset email to this user?')) return;

    try {
      await resetPasswordMutation.mutateAsync(userId);
      success('Password reset email sent successfully!');
      setUserMenuOpen(null);
    } catch (err: any) {
      console.error('Failed to reset password:', err);
      showError('Failed to reset password', err.message || 'Please try again');
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    // Show detailed confirmation dialog FIRST
    const confirmed = window.confirm(
      `Delete user "${userName}"?\n\n` +
      `This will permanently remove:\n` +
      `• User account and profile\n` +
      `• All associated permissions\n` +
      `• System access\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) {
      return; // User cancelled - do nothing
    }

    // User confirmed - proceed with deletion
    try {
      await deleteUserMutation.mutateAsync(userId);

      // Track user deletion
      logChange({
        action: 'deleted',
        entityType: 'user',
        entityName: userName,
        description: `deleted user ${userName}`,
        performedBy: 'Admin'
      });

      success('User deleted successfully!', `${userName} has been permanently removed`);
      setUserMenuOpen(null);
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      showError('Failed to delete user', err.message || 'Please try again');
    }
  };

  const handleMenuToggle = (userId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    // If clicking the same menu, close it
    if (userMenuOpen === userId) {
      setUserMenuOpen(null);
      return;
    }

    // Get button position
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    // Calculate available space
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Estimate menu height (approximately 300px for all options including delete, with padding)
    const menuHeight = 300;
    const minSpaceRequired = 50; // Minimum space needed to show menu

    // Calculate menu position
    let position: 'top' | 'bottom' = 'bottom';
    let menuTop = rect.bottom + 8; // Default: show below button
    let menuLeft = rect.right - 192; // Align right edge (192px = w-48)

    // Decide position: if not enough space below, open upward
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      position = 'top';
      menuTop = rect.top - menuHeight - 8; // Show above button
    } else if (spaceBelow < minSpaceRequired && spaceAbove > menuHeight) {
      position = 'top';
      menuTop = rect.top - menuHeight - 8;
    }

    // Ensure menu doesn't go off-screen horizontally
    if (menuLeft < 8) {
      menuLeft = 8; // Minimum left margin
    } else if (menuLeft + 192 > window.innerWidth - 8) {
      menuLeft = window.innerWidth - 192 - 8; // Keep right margin
    }

    // Ensure menu doesn't go off-screen vertically
    if (menuTop < 8) {
      menuTop = 8; // Minimum top margin
    } else if (menuTop + menuHeight > window.innerHeight - 8) {
      menuTop = window.innerHeight - menuHeight - 8; // Keep bottom margin
    }

    setMenuPosition(position);
    setMenuCoordinates({ top: menuTop, left: menuLeft });
    setUserMenuOpen(userId);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Users</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Manage user accounts and permissions
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            New User
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource="mockDataManager.ts"
          itemCount={users ? `${users.length} users` : 'users'}
          customMessage={`Development Mode: ${users ? `Showing ${users.length} users` : 'Using mock data'} persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {users?.length || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Users</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <UserIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {activeUsers}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Active Users</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {inactiveUsers}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Inactive Users</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search users by name, email, username, department..."
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />

            <StandardSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as UserStatus)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
            />

            <StandardSelect
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              options={[
                { value: '', label: 'All Roles' },
                ...roles.map(role => ({ value: role.id, label: role.name }))
              ]}
            />

            <StandardSelect
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              options={[
                { value: '', label: 'All Departments' },
                ...departments.map(dept => ({ value: dept, label: dept }))
              ]}
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading users..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading users"
          message="Please try again."
        />
      )}

      {/* Users List */}
      {!isLoading && !error && (
        <>
          {users && users.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto overflow-y-visible">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="w-[20%] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="w-[25%] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="w-[15%] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="w-[12%] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="w-[13%] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="w-[10%] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="w-[80px] min-w-[80px] px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      onView={() => setViewingUser(user)}
                      onEdit={() => setEditingUser(user)}
                      onViewAsUser={() => handleViewAsUser(user)}
                      onToggleStatus={() => handleToggleStatus(user.id)}
                      onResetPassword={() => handleResetPassword(user.id)}
                      onDelete={() => handleDeleteUser(user.id, getUserFullName(user))}
                      menuOpen={userMenuOpen === user.id}
                      menuPosition={menuPosition}
                      menuCoordinates={menuCoordinates}
                      onMenuToggle={(e) => handleMenuToggle(user.id, e)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <StandardEmptyState
              icon={<UserIcon className="w-16 h-16" />}
              title="No users found"
              message={searchQuery || statusFilter !== 'all' || roleFilter || departmentFilter
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by creating your first user'
              }
              actionLabel={searchQuery || statusFilter !== 'all' || roleFilter || departmentFilter ? 'Clear filters' : undefined}
              onAction={searchQuery || statusFilter !== 'all' || roleFilter || departmentFilter
                ? () => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setRoleFilter('');
                    setDepartmentFilter('');
                  }
                : undefined
              }
            />
          )}
        </>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <EditUserModal
        user={editingUser}
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
      />

      <UserProfileModal
        user={viewingUser}
        isOpen={!!viewingUser}
        onClose={() => setViewingUser(null)}
        onEdit={() => {
          setEditingUser(viewingUser);
          setViewingUser(null);
        }}
      />
    </div>
  );
};

// User Row Component
interface UserRowProps {
  user: User;
  onView: () => void;
  onEdit: () => void;
  onViewAsUser: () => void;
  onToggleStatus: () => void;
  onResetPassword: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  menuPosition: 'top' | 'bottom';
  menuCoordinates: { top: number; left: number } | null;
  onMenuToggle: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  onView,
  onEdit,
  onViewAsUser,
  onToggleStatus,
  onResetPassword,
  onDelete,
  menuOpen,
  menuPosition,
  menuCoordinates,
  onMenuToggle
}) => {
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold flex-shrink-0">
            {getUserInitials(user)}
          </div>
          <div className="min-w-0">
            <button
              onClick={onView}
              className="font-medium text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 transition-colors truncate block w-full text-left"
            >
              {getUserFullName(user)}
            </button>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {user.username}
            </div>
          </div>
        </div>
      </td>

      <td className="px-6 py-4">
        <div className="text-sm min-w-0">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white mb-1 min-w-0">
            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="truncate">{user.email}</span>
          </div>
          {user.phone && (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 min-w-0">
              <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">{user.phone}</span>
            </div>
          )}
        </div>
      </td>

      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {user.roleNames && user.roleNames.length > 0 ? (
            user.roleNames.map((roleName, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
              >
                {roleName}
              </span>
            ))
          ) : user.roleName ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              {user.roleName}
            </span>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
          )}
        </div>
        {user.title && (
          <div className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1">{user.title}</div>
        )}
      </td>

      <td className="px-6 py-4">
        <div className="text-sm text-gray-900 dark:text-white truncate">
          {user.department || '-'}
        </div>
      </td>

      <td className="px-6 py-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {formatLastLogin(user.lastLogin)}
        </div>
      </td>

      <td className="px-6 py-4">
        {user.isActive ? (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
            Active
          </span>
        ) : (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400">
            Inactive
          </span>
        )}
      </td>

      <td className="px-6 py-4 text-right">
        <div className="relative inline-block">
          <button
            onClick={onMenuToggle}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>

          {menuOpen && menuCoordinates && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => onMenuToggle(e as any)}
              />
              <div
                className="fixed w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 transition-all duration-150 max-h-[calc(100vh-2rem)] overflow-y-auto"
                style={{
                  top: `${menuCoordinates.top}px`,
                  left: `${menuCoordinates.left}px`,
                  maxHeight: 'min(calc(100vh - 1rem), 350px)',
                }}
              >
                <div className="py-1">
                  <button
                    onClick={onView}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View Profile
                  </button>
                  <button
                    onClick={onEdit}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit User
                  </button>
                  {/* PE-762: View As User action */}
                  <button
                    onClick={onViewAsUser}
                    className="w-full px-4 py-2 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
                  >
                    <UserCog className="w-4 h-4" />
                    View As User
                  </button>
                  <button
                    onClick={onResetPassword}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Reset Password
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                  <button
                    onClick={onToggleStatus}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                      user.isActive
                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                        : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                  >
                    {user.isActive ? (
                      <>
                        <X className="w-4 h-4" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Activate
                      </>
                    )}
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                  <button
                    onClick={onDelete}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete User
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};
